import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { roleGuard } from './guards/role.guard';
import { UserRole } from './models/user.model';

export const appRoutes: Routes = [
  {
    // SCRUM-813 — lazy-loaded so NetworkDiagnosticsComponent + its ~9 kB chunk
    // never enter the initial bundle. Only fetched when a user hits /diagnostics.
    path: 'diagnostics',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./components/network-diagnostics.component').then(
        (m) => m.NetworkDiagnosticsComponent,
      ),
  },
  {
    // Car-Native UI (CAR_MODE_REDESIGN_EPIC) — purpose-built glanceable car
    // view. Lazy so its chunk never enters the desktop bundle. Auth-gated like
    // every real page. /car = glance Home; /car/why = posture drill-in;
    // /car/menu = big-tap nav grid.
    path: 'car',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/car/car-home.component').then((m) => m.CarHomeComponent),
  },
  {
    path: 'car/why',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/car/car-why.component').then((m) => m.CarWhyComponent),
  },
  {
    path: 'car/menu',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/car/car-menu.component').then((m) => m.CarMenuComponent),
  },
  {
    // BRIEF_ANALYSIS_REDESIGN_P1_SPEC RDS-16 — car ticker glance (verdict + 3 facts).
    path: 'car/ticker/:symbol',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/car/car-ticker.component').then((m) => m.CarTickerComponent),
  },
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full',
  },
  {
    path: 'portfolio',
    redirectTo: '/portfolios',
    pathMatch: 'full',
  },
  {
    path: 'profile',
    redirectTo: '/pages/profile',
    pathMatch: 'full',
  },
  {
    path: 'auth',
    loadChildren: () => import('./features/auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    // US-14: public read-only watchlist share link. No auth guard — the
    // server validates the short code and strips notes/alerts before
    // responding.
    path: 'w/:code',
    loadComponent: () =>
      import('./features/watchlist/pages/public-watchlist.component').then((m) => m.PublicWatchlistComponent),
  },
  {
    path: 'dashboard',
    canActivate: [authGuard],
    loadChildren: () => import('./features/dashboard/dashboard.routes').then((m) => m.DASHBOARD_ROUTES),
  },
  {
    path: 'portfolios',
    canActivate: [authGuard],
    loadChildren: () => import('./features/portfolios/portfolio.routes').then((m) => m.PORTFOLIO_ROUTES),
  },
  {
    path: 'watchlist',
    canActivate: [authGuard],
    loadChildren: () => import('./features/watchlist/watchlist.routes').then(m => m.WATCHLIST_ROUTES),
  },
  {
    // DIVIDEND_INCOME_SPEC — premarket dividend analyzer + buy-timing page.
    path: 'dividend-income',
    canActivate: [authGuard],
    loadChildren: () => import('./features/dividend-income/dividend-income.routes').then((m) => m.DIVIDEND_INCOME_ROUTES),
  },
  {
    path: 'strategies',
    canActivate: [authGuard, roleGuard([UserRole.ADMIN])],
    loadChildren: () => import('./features/strategies/strategies.routes').then((m) => m.STRATEGIES_ROUTES),
  },
  {
    path: 'research',
    canActivate: [authGuard],
    loadChildren: () => import('./features/research/research.routes').then((m) => m.RESEARCH_ROUTES),
  },
  {
    // SCRUM-1614 (epic SCRUM-1609) — first-principles stock report page.
    path: 'reports',
    canActivate: [authGuard],
    loadChildren: () => import('./features/reports/reports.routes').then((m) => m.REPORTS_ROUTES),
  },
  {
    path: 'options-analysis',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/options-analysis/options-analysis.routes').then((m) => m.OPTIONS_ANALYSIS_ROUTES),
  },
  {
    // SCRUM-2319 — short, linkable URL for the "How the Analysis Works"
    // explainer so it resolves at /analysis/how-it-works. Same auth as every
    // other page (authGuard). Also reachable under /options-analysis/how-it-works.
    path: 'analysis/how-it-works',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/options-analysis/pages/how-it-works.component').then((m) => m.HowItWorksComponent),
  },
  {
    // SCRUM-2165 — a user's personal six-factor verdict weighting.
    path: 'my-analysis-weights',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/options-analysis/pages/user-analysis-weights.component').then((m) => m.UserAnalysisWeightsComponent),
  },
  {
    path: 'income-dashboard',
    canActivate: [authGuard, roleGuard([UserRole.ADMIN])],
    loadChildren: () => import('./features/income/income.routes').then((m) => m.INCOME_ROUTES),
  },
  // SCRUM-906 (2026-06-09) — /backtest route + features/backtest module removed
  // with the rest of the Trade section. 0 rows in strategy_backtests in dev+UAT.
  {
    path: 'pre-market',
    canActivate: [authGuard, roleGuard([UserRole.ADMIN])],
    loadChildren: () => import('./features/pre-market/pre-market.routes').then((m) => m.PRE_MARKET_ROUTES),
  },
  {
    path: 'flow-scanner',
    canActivate: [authGuard, roleGuard([UserRole.ADMIN])],
    loadChildren: () => import('./features/flow-scanner/flow-scanner.routes').then((m) => m.FLOW_SCANNER_ROUTES),
  },
  {
    // SCRUM-882 (2026-06-09) — /positions merged into the Portfolio page as
    // a tab. Legacy route redirects so existing bookmarks / deep links land
    // on the right surface (the /portfolios/all page reads ?tab=positions).
    path: 'positions',
    redirectTo: '/portfolios/all?tab=positions',
    pathMatch: 'full',
  },
  {
    // VWAP_ALERTS_SPEC Feature 2 — price alert management page.
    path: 'alerts',
    canActivate: [authGuard, roleGuard([UserRole.ADMIN])],
    loadChildren: () => import('./features/alerts/alerts.routes').then((m) => m.ALERTS_ROUTES),
  },
  {
    // Earnings Intel merged into /calendar — redirect legacy route.
    path: 'earnings',
    redirectTo: 'calendar',
    pathMatch: 'full',
  },
  {
    // SENTIMENT_INTELLIGENCE_SPEC — sentiment dashboard.
    path: 'sentiment',
    canActivate: [authGuard, roleGuard([UserRole.ADMIN])],
    loadChildren: () =>
      import('./features/sentiment-intel/sentiment-intel.routes').then((m) => m.SENTIMENT_INTEL_ROUTES),
  },
  {
    // SENTIMENT_INTELLIGENCE_SPEC — cult-stock radar.
    path: 'cult',
    canActivate: [authGuard],
    loadChildren: () => import('./features/cult/cult.routes').then((m) => m.CULT_ROUTES),
  },
  {
    // UNIFIED_CALENDAR_SPEC — earnings + macro events combined.
    path: 'calendar',
    canActivate: [authGuard],
    loadChildren: () => import('./features/calendar/calendar.routes').then((m) => m.CALENDAR_ROUTES),
  },
  {
    // SCRUM-1862 — Funds: top institutional investors (13F), under Calendar.
    path: 'funds',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/funds/pages/funds.component').then((m) => m.FundsComponent),
  },
  {
    // UNIFIED_CALENDAR_SPEC — old earnings calendar redirects to unified page.
    path: 'earnings-calendar',
    redirectTo: 'calendar',
    pathMatch: 'full',
  },
  {
    // MORNING_OVERVIEW_AND_SEC_SPEC Phase 3.2 — sector heatmap page.
    path: 'sector-heatmap',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/sector-heatmap/pages/sector-heatmap.component').then(
        (m) => m.SectorHeatmapComponent,
      ),
  },
  {
    // UX_FIXES_AND_SECTOR_DETAIL_SPEC R4.3 — per-sector detail page.
    path: 'sector-detail/:sector',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/sector-heatmap/pages/sector-detail.component').then(
        (m) => m.SectorDetailComponent,
      ),
  },
  {
    // MORNING_DASHBOARD_SPEC Feature 2 — Weekly Growth Stock Scanner.
    path: 'scanner',
    canActivate: [authGuard, roleGuard([UserRole.ADMIN])],
    loadChildren: () => import('./features/scanner/scanner.routes').then((m) => m.SCANNER_ROUTES),
  },
  {
    // MORNING_OVERVIEW_AND_SEC_SPEC Phase 4.5 — SEC daily filings page.
    path: 'sec/daily',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/sec-filings/pages/sec-daily.component').then(
        (m) => m.SecDailyComponent,
      ),
  },
  {
    // SEC_FILINGS_SPEC — 13F merged into SEC daily page (Institutional tab).
    path: 'sec/13f',
    redirectTo: 'sec/daily',
    pathMatch: 'full',
  },
  {
    // SCRUM-2014 — IPO briefings (AI S-1 analysis).
    path: 'ipo/briefings',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/ipo/pages/ipo-briefings.component').then((m) => m.IpoBriefingsComponent),
  },
  {
    // UNIFIED_CALENDAR_SPEC — old economic calendar redirects to unified page.
    path: 'economic-calendar',
    redirectTo: 'calendar',
    pathMatch: 'full',
  },
  {
    // FAST_DECISION_SPEC Feature 9 — portfolio risk dashboard.
    path: 'risk-dashboard',
    canActivate: [authGuard, roleGuard([UserRole.ADMIN])],
    loadComponent: () =>
      import('./features/risk/pages/risk-dashboard.component').then(
        (m) => m.RiskDashboardComponent,
      ),
  },
  {
    // SCRUM-1769 (epic SCRUM-1755) — Morning Overview accuracy trend page.
    path: 'overview-accuracy',
    canActivate: [authGuard, roleGuard([UserRole.ADMIN])],
    loadComponent: () =>
      import('./features/overview-accuracy/pages/overview-accuracy.component').then(
        (m) => m.OverviewAccuracyComponent,
      ),
  },
  // SCRUM-882 (2026-06-09) — /tracker (Trade Tracker, FAST_DECISION_SPEC F13)
  // route + module + backend endpoints + tracker_trades table removed.
  // Zero rows in dev + UAT confirmed before deletion; no migration needed.
  // Trade Journal at /journal covers the same authoring need.
  {
    // ADVANCED_FEATURES_SPEC Feature 3 — AI Trade Journal.
    path: 'journal',
    canActivate: [authGuard, roleGuard([UserRole.ADMIN])],
    loadComponent: () =>
      import('./features/journal/pages/journal.component').then(
        (m) => m.JournalComponent,
      ),
  },
  // SCRUM-906 (2026-06-09) — /order-builder + /strategy-templates routes
  // removed with the Trade section. ADVANCED_FEATURES_SPEC Features 5 + 13
  // retired; 0 user-created rows in strategy_templates (5 system templates
  // dropped via migration with the table).
  {
    // ADVANCED_FEATURES_SPEC Feature 14 — Tax Lot Tracking.
    path: 'tax',
    canActivate: [authGuard, roleGuard([UserRole.ADMIN])],
    loadComponent: () =>
      import('./features/tax/pages/tax.component').then(
        (m) => m.TaxComponent,
      ),
  },
  {
    path: 'news',
    canActivate: [authGuard],
    loadChildren: () => import('./features/news/news.routes').then((m) => m.NEWS_ROUTES),
  },
  {
    path: 'ui-components',
    canActivate: [authGuard],
    loadChildren: () => import('./features/ui-components/ui-components.routes').then((m) => m.UI_COMPONENTS_ROUTES),
  },
  {
    path: 'utilities',
    canActivate: [authGuard],
    loadChildren: () => import('./features/utilities/utilities.routes').then((m) => m.UTILITIES_ROUTES),
  },
  {
    path: 'pages',
    canActivate: [authGuard],
    loadChildren: () => import('./features/pages/pages.routes').then((m) => m.PAGES_ROUTES),
  },
  {
    // Admin children include: admin/users, admin/qa, admin/pipeline, admin/bridge
    path: 'admin',
    canActivate: [authGuard, roleGuard([UserRole.ADMIN])],
    loadChildren: () => import('./features/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
  },
  {
    path: '**',
    redirectTo: '/dashboard',
  },
];
